import React from "react";
import { MeLocationService, PlaceSuggestion } from "../../../api/meLocation.service";

/**
 * Debounced live place search for one Where-to field (pickup index 0,
 * drop-off/stop index 1). Mirrors the qa-api-tools tester
 * (socket-tester/src/place-search.tsx search()) but fires on typing with a
 * ~250ms debounce instead of an explicit Search button.
 *
 * Auth: the v3 autocomplete route needs the rider Firebase ID token, so while
 * signed out the hook returns no suggestions (the caller shows signed-out UI).
 */
export function usePlaceSearch(args: {
  query: string;
  index: 0 | 1;
  signedIn: boolean;
  lat?: number;
  lng?: number;
  enabled?: boolean;
}): { suggestions: PlaceSuggestion[]; searching: boolean; searchError: boolean } {
  const { query, index, signedIn, lat, lng, enabled = true } = args;
  const [suggestions, setSuggestions] = React.useState<PlaceSuggestion[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState(false);
  const requestId = React.useRef(0);

  React.useEffect(() => {
    const trimmed = query.trim();
    const id = ++requestId.current;
    if (!enabled || !signedIn || !trimmed) {
      setSuggestions([]);
      setSearching(false);
      setSearchError(false);
      return () => {
        if (requestId.current === id) requestId.current += 1;
      };
    }
    setSuggestions([]);
    setSearchError(false);
    setSearching(true);
    const timer = setTimeout(() => {
      const service = new MeLocationService();
      const locationBias = lat != null && lng != null ? { lat, lng } : {};
      service
        .placeAutocomplete({ query: trimmed, index, ...locationBias })
        .then((rows) => {
          if (requestId.current !== id) return;
          setSuggestions(rows);
          setSearchError(false);
          setSearching(false);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setSuggestions([]);
          setSearchError(true);
          setSearching(false);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      if (requestId.current === id) requestId.current += 1;
    };
  }, [query, index, signedIn, lat, lng, enabled]);

  return { suggestions, searching, searchError };
}
